import {
  APPLICATION_KEY,
  SINGLE_APPLICATION_KEY,
  selectChecklistApplication,
} from "@fraction/shared-frontend";
import type { Query, QueryFunction, QueryFunctionContext, QueryKey } from "@tanstack/react-query";
import { experimental_createQueryPersister, type PersistedQuery } from "@tanstack/react-query-persist-client";
import { persistedJsonReplacer, persistedJsonReviver } from "./persistedJson";
import PersisterWorker from "./queryPersister.worker?worker";

const worker = new PersisterWorker();
let stopped = false;

type PendingEntry = {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};
const pending = new Map<number, PendingEntry>();
let nextRequestId = 0;

// The persister gates every query's queryFn behind a worker round-trip. If
// IndexedDB or the worker wedges (e.g. a blocked `deleteDatabase` leaves the
// store locked), a request can never get a reply — without a cap that would
// freeze ALL loading indefinitely. So bound each request and fall back to a
// cache miss (`get`) / no-op (`set`/`del`) so the app keeps working off the
// network instead of hanging.
const WORKER_TIMEOUT_MS = 3000;

function settle(id: number, settler: (entry: PendingEntry) => void) {
  const entry = pending.get(id);
  if (!entry) return;
  pending.delete(id);
  clearTimeout(entry.timer);
  settler(entry);
}

worker.addEventListener(
  "message",
  (event: MessageEvent<{ id: number; result?: unknown; error?: string }>) => {
    const { id, result, error } = event.data;
    settle(id, (entry) => (error ? entry.reject(new Error(error)) : entry.resolve(result)));
  }
);

// A worker-level failure (script error, etc.) never delivers per-request
// replies — release everything in flight as a cache miss so queries fall
// through to the network rather than stalling.
worker.addEventListener("error", () => {
  for (const id of [...pending.keys()]) settle(id, (entry) => entry.resolve(undefined));
});

function callWorker<T>(op: "get" | "set" | "del", key: string, value?: unknown): Promise<T> {
  if (stopped) return Promise.resolve(undefined as T);
  const id = ++nextRequestId;
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      // Timed out — a late reply (if any) is ignored since the entry is gone.
      settle(id, (entry) => entry.resolve(undefined));
    }, WORKER_TIMEOUT_MS);
    pending.set(id, { resolve: resolve as (v: unknown) => void, reject, timer });
    worker.postMessage({ id, op, key, value });
  });
}

const storage = {
  getItem: (key: string) => callWorker<string | undefined | null>("get", key),
  setItem: (key: string, value: string) => callWorker<void>("set", key, value),
  removeItem: (key: string) => callWorker<void>("del", key),
};

const serialize = (q: PersistedQuery) => JSON.stringify(q, persistedJsonReplacer);
/**
 * Rebuild restored applications through the entity, as the API client does on fetch. The tags only
 * revive what was a Decimal or Date when the row was written: a build that didn't yet declare a
 * column persisted it as raw JSON, and restored data reaches the page without another
 * `plainToInstance`. That is how a newly added Decimal column (`registrationPercentage`) arrived at
 * `.toNumber()` as a number on the first launch after an upgrade.
 */
const hydrateApplications = (q: PersistedQuery): PersistedQuery => {
  const root = q.queryKey[0];
  const data: unknown = q.state.data;
  if (!data || typeof data !== "object") {
    return q;
  }
  if (root === SINGLE_APPLICATION_KEY[0] && !Array.isArray(data)) {
    q.state.data = selectChecklistApplication(data);
  } else if (root === APPLICATION_KEY[0] && "data" in data && Array.isArray(data.data)) {
    q.state.data = { ...data, data: data.data.map((app) => selectChecklistApplication(app)) };
  }
  return q;
};

export const deserialize = (s: string) =>
  hydrateApplications(JSON.parse(s, persistedJsonReviver) as PersistedQuery);

const rawPersister = experimental_createQueryPersister({
  storage,
  // Entity-keyed: a build whose entities changed drops what the previous build saved rather than
  // restoring rows that predate its columns.
  buster: `v5-${__ENTITY_SCHEMA_HASH__}`,
  maxAge: 1000 * 60 * 60 * 24,
  serialize,
  deserialize,
});

// `@tanstack/react-query-persist-client` (5.97) ships its own copy of `query-core`,
// so `rawPersister`'s signatures reference a different `Query`/`QueryClient` class
// than the pinned `@tanstack/react-query` (5.90). Runtime they're the same thing —
// re-type at the boundary using react-query's own types to bridge the two.
type ReactQueryPersisterFn = <T, TQueryKey extends QueryKey>(
  queryFn: QueryFunction<T, TQueryKey>,
  context: QueryFunctionContext<TQueryKey>,
  query: Query
) => Promise<T>;

export const queryPersister = {
  persisterFn: rawPersister.persisterFn as unknown as ReactQueryPersisterFn,
  persistQuery: (query: Query): Promise<void> => {
    if (stopped) return Promise.resolve();
    return (rawPersister.persistQuery as unknown as (query: Query) => Promise<void>)(query);
  },
  stop: () => {
    if (stopped) return;
    stopped = true;
    worker.terminate();
    for (const id of [...pending.keys()]) settle(id, (entry) => entry.resolve(undefined));
  },
};
